import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import Swal from 'sweetalert2';
import {
  passwordResetInitiate,
  passwordResetVerify,
  passwordResetConfirm,
} from '../../utils/urls';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import FlexComponent from '../FlexComponent';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ForgotPassword({ onBackToLogin }) {
  // 'identify' -> 'otp' -> 'reset' -> 'done'
  const [step, setStep] = useState('identify');

  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [otpSessionId, setOtpSessionId] = useState(null);
  const [channels, setChannels] = useState([]);
  const [resendTimer, setResendTimer] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Resend timer countdown (mirrors the OTP login flow)
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // ==================== Step 1: Send OTP ====================
  const initiateReset = async () => {
    const value = identifier.trim();
    if (!value) {
      setError('Please enter your username or email');
      return;
    }
    // Validate email format only when an email is entered (accounts may use a username)
    if (value.includes('@') && !EMAIL_REGEX.test(value)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');

    try {
      const res = await axios.post(passwordResetInitiate, { username: value });
      setOtpSessionId(res.data?.otp_session_id || null);
      setChannels(res.data?.channels || []);
      setInfo(res.data?.detail || 'If an account was found, an OTP has been sent.');
      setStep('otp');
      setResendTimer(60);
      setOtp('');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong. Please try again.';
      setError(detail);
      Swal.fire({ icon: 'error', title: 'Error', text: detail });
    } finally {
      setLoading(false);
    }
  };

  // ==================== Resend OTP ====================
  const handleResend = async () => {
    if (resendTimer > 0 || loading) return;
    await initiateReset();
  };

  // ==================== Step 2: Verify OTP ====================
  const verifyOtp = async () => {
    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');

    try {
      const res = await axios.post(passwordResetVerify, {
        otp_session_id: otpSessionId,
        otp: otp.trim(),
      });
      // Backend echoes the session id back; keep the latest one.
      if (res.data?.otp_session_id) setOtpSessionId(res.data.otp_session_id);
      setInfo(res.data?.detail || 'OTP verified.');
      setStep('reset');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid or expired OTP';
      setError(detail);
      Swal.fire({ icon: 'error', title: 'Invalid OTP', text: detail });
    } finally {
      setLoading(false);
    }
  };

  // ==================== Step 3: Reset Password ====================
  const confirmReset = async () => {
    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');

    try {
      const res = await axios.post(passwordResetConfirm, {
        otp_session_id: otpSessionId,
        new_password: newPassword,
      });
      setInfo(res.data?.detail || 'Password reset successful.');
      setStep('done');
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: res.data?.detail || 'Password reset successful.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not reset password. Please try again.';
      setError(detail);
      Swal.fire({ icon: 'error', title: 'Error', text: detail });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    if (step === 'identify') initiateReset();
    else if (step === 'otp') verifyOtp();
    else if (step === 'reset') confirmReset();
  };

  const inputStyle = { width: '100%', marginBottom: '1rem' };
  const submitButtonStyle = {
    width: '100%',
    marginTop: '1.5rem',
    background: 'white',
    color: 'black',
    fontWeight: '500',
    padding: '12px',
  };

  return (
    <FlexComponent justifyContent="center">
      <form onSubmit={handleSubmit} style={{ zIndex: '1', width: '100%', maxWidth: '420px' }}>
        <p style={{ fontSize: '18px', color: 'white', textAlign: 'center' }}>
          Reset your password
        </p>

        {/* Step 1: Identify account */}
        {step === 'identify' && (
          <div style={{ marginTop: '1.5rem' }}>
            <label htmlFor="fp-identifier" style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '0.5rem' }}>
              Username or Email
            </label>
            <InputText
              id="fp-identifier"
              type="text"
              autoComplete="username"
              placeholder="Enter your username or email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              aria-invalid={!!error}
              style={inputStyle}
            />
          </div>
        )}

        {/* Step 2: Verify OTP */}
        {step === 'otp' && (
          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            {info && <p style={{ color: 'white', marginBottom: '0.5rem' }}>{info}</p>}
            {channels?.length > 0 && (
              <p style={{ fontWeight: 'bold', marginBottom: '1rem', color: 'white' }}>
                {channels
                  .map((ch) => `${ch.type?.toUpperCase()} — ${ch.masked_value}`)
                  .join(', ')}
              </p>
            )}

            <label htmlFor="fp-otp" className="sr-only" style={{ position: 'absolute', left: '-9999px' }}>
              One-time password
            </label>
            <InputText
              id="fp-otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              aria-invalid={!!error}
              style={{ width: '100%', fontSize: '1.8rem', textAlign: 'center', letterSpacing: '25px' }}
            />

            <Button
              type="button"
              label={resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
              disabled={resendTimer > 0 || loading}
              className="p-button-text p-button-sm"
              onClick={handleResend}
              style={{ marginTop: '1rem', color: 'white', border: resendTimer === 0 ? '1px solid grey' : 'none' }}
            />
          </div>
        )}

        {/* Step 3: Reset password */}
        {step === 'reset' && (
          <div style={{ marginTop: '1.5rem' }}>
            <label htmlFor="fp-new-password" style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '0.5rem' }}>
              New Password
            </label>
            <Password
              inputId="fp-new-password"
              placeholder="New password"
              feedback={false}
              toggleMask
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              aria-invalid={!!error}
              style={{ width: '100%', marginBottom: '1rem' }}
              inputStyle={{ width: '100%' }}
            />

            <label htmlFor="fp-confirm-password" style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '0.5rem' }}>
              Confirm Password
            </label>
            <Password
              inputId="fp-confirm-password"
              placeholder="Confirm password"
              feedback={false}
              toggleMask
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              aria-invalid={!!error}
              style={{ width: '100%' }}
              inputStyle={{ width: '100%' }}
            />
          </div>
        )}

        {/* Step 4: Done */}
        {step === 'done' && (
          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <i className="pi pi-check-circle" style={{ fontSize: '2.5rem', color: '#4ade80' }} />
            <p style={{ color: 'white', margin: '1rem 0' }}>
              {info || 'Password reset successful.'}
            </p>
          </div>
        )}

        {error && (
          <p role="alert" style={{ color: '#ff6b6b', textAlign: 'center', margin: '1rem 0' }}>
            {error}
          </p>
        )}

        {/* Primary action */}
        {step !== 'done' && (
          <Button
            type="submit"
            loading={loading}
            label={
              step === 'identify'
                ? 'Send OTP'
                : step === 'otp'
                ? 'Verify OTP'
                : 'Reset Password'
            }
            style={submitButtonStyle}
          />
        )}

        {/* Back to login */}
        <Button
          type="button"
          label={step === 'done' ? 'Back to Login' : '← Back to Login'}
          className="p-button-text p-button-sm"
          onClick={onBackToLogin}
          style={{ width: '100%', marginTop: '0.8rem', color: 'white' }}
        />
      </form>
    </FlexComponent>
  );
}

export default ForgotPassword;
